import type { Response, ResponseError } from 'superagent';

export * from './guards/index';
export * from './html/index';
export * from './intl/message';
export * from './locale-bundle/index';
export * from './services/google/gmail';
export * from './services/sentry';

export type NumberEnumValue<T extends { [key: string]: number }> = T[keyof T];

export type Nullable<T> = T | null;

export type ColorValueHex = `#${string}`;

export function reverseRecord<T extends PropertyKey, U extends PropertyKey>(input: Record<T, U>) {
  return Object.fromEntries(Object.entries(input).map(([key, value]) => [value, key])) as Record<
    U,
    T
  >;
}

/**
 * @interface TypedResponse
 * @extends Response
 * @description A typed extension of the SuperAgent `Response` interface, allowing for type-safe handling of the response body.
 *
 * @generic T - The expected type of the response body.
 *
 * @property body - The response body, typed according to the specified generic type `T`.
 */
export interface TypedResponse<T> extends Response {
  body: T;
}

export interface TypedResponseError<T> extends ResponseError {
  body: T;
}

/**
 * @interface SuperAgentError
 * @extends Error
 * @description A custom error interface representing errors returned by SuperAgent requests.
 *
 * @property {number} [status] - The HTTP status code of the error response, if available.
 * @property {TypedResponse<Object>} [response] - The SuperAgent response object containing error details.
 * @property {Object} response.body - The structured error response body.
 * @property {string} response.body.message - A descriptive error message explaining the issue.
 * @property {number} response.body.code - The HTTP or application-specific error code.
 * @property {string} response.body.type - A short identifier for the error type (e.g., `NOT_FOUND`).
 * @property {any} [response.body.more] - Additional details about the error, if applicable.
 */
export interface SuperAgentError extends Error {
  status?: number;
  response?: TypedResponseError<{
    message: string;
    code: number;
    type: string;
    // biome-ignore lint/suspicious/noExplicitAny: should have a better type
    more?: any;
  }>;
}
